import React, { useEffect, useRef } from 'react';
import { motion, useIsPresent } from 'framer-motion';
import NeapolitanCard from './NeapolitanCard';
import { cardsLabel } from '../game/cards';

// Presents every valid capture combination as a navigable choice.
// No "confirm" step: activating an option executes the capture immediately.
export default function CaptureChoice({ options, onChoose, onCancel }) {
  const firstRef = useRef(null);
  const openedAt = useRef(Date.now());
  // While AnimatePresence plays the exit animation the component stays mounted;
  // isPresent turns false at that moment. We use it to immediately drop the
  // whole dialog subtree (options included) from the accessibility tree via
  // inert + aria-hidden, so VoiceOver can no longer swipe back to the choices
  // that were already made — without removing the visual fade-out.
  const isPresent = useIsPresent();

  useEffect(() => {
    openedAt.current = Date.now();
    if (firstRef.current) firstRef.current.focus();
  }, []);

  // Activation guard: ignore any choice within a short window after the sheet
  // opens. This defeats the stray SECOND tap of an iPhone/VoiceOver double-tap
  // (the habitual gesture), which would otherwise land on an option button and
  // play an irreversible capture the user never actually chose. The sheet is
  // also centred (not bottom-aligned) so it no longer overlaps the hand row.
  const guardedChoose = (i) => {
    if (Date.now() - openedAt.current < 450) return;
    onChoose(i);
  };

  return (
    <motion.div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label="Scegli quale presa effettuare"
      aria-hidden={!isPresent || undefined}
      inert={!isPresent ? true : undefined}
      data-testid="capture-choice"
    >
      <motion.div
        className="w-full max-w-md rounded-2xl bg-[#1c2a20] border border-[#D4AF37]/30 shadow-2xl p-4"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 30, opacity: 0 }}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-serif text-lg text-[#FDFBF7]">
            Quale presa vuoi effettuare?
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-[#E0E0D1]/70 text-sm px-2 py-1 rounded-lg hover:bg-white/10 transition-colors"
            data-testid="capture-cancel"
            aria-label="Annulla la selezione"
          >
            Annulla
          </button>
        </div>
        <div className="flex flex-col gap-2.5" role="list">
          {options.map((option, i) => (
            <button
              key={i}
              ref={i === 0 ? firstRef : null}
              type="button"
              onClick={() => guardedChoose(i)}
              data-testid={`capture-option-${i}`}
              aria-label={`Prendi ${cardsLabel(option)}`}
              className="flex items-center gap-3 w-full text-left rounded-xl bg-[#14452F]/70 border border-[#D4AF37]/20 p-2.5 outline-none transition-colors hover:bg-[#14452F] focus-visible:ring-[3px] focus-visible:ring-[#F2D675]"
            >
              <div className="flex -space-x-3">
                {option.map((card) => (
                  <NeapolitanCard
                    key={card.id}
                    card={card}
                    sizeClass="w-10"
                    className="shrink-0"
                  />
                ))}
              </div>
              <span className="font-sans text-sm text-[#FDFBF7] flex-1">
                {cardsLabel(option)}
              </span>
            </button>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
}
